import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  FaSignInAlt, FaSignOutAlt, FaEnvelope, FaLock,
  FaCheck, FaTimes, FaPhone, FaUser, FaMapMarkerAlt,
  FaStickyNote, FaUtensils, FaClock, FaExclamationTriangle,
  FaSync, FaChevronDown, FaChevronUp, FaHashtag,
  FaChartBar, FaClipboardList, FaCog, FaBell,
  FaTruck, FaCheckCircle, FaFire, FaSearch,
  FaPause, FaPlay, FaEdit, FaSave, FaToggleOn, FaToggleOff,
  FaEuroSign, FaMapPin, FaBan, FaHistory, FaVolumeUp, FaVolumeMute,
  FaPlus, FaTrash,
} from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';
import { useAuth, authAxios } from '../context/AuthContext';
import { MENU_DATA, CATEGORY_META } from '../components/menuData';
import './AdminDashboard.css';

/* ================================================================
   STATUS CONFIGS
   ================================================================ */
const STATUS_CONFIG = {
  pending:   { label: 'Pending',   className: 'status-pending',   icon: <FaClock /> },
  confirmed: { label: 'Accepted',  className: 'status-confirmed', icon: <FaCheck /> },
  preparing: { label: 'Preparing', className: 'status-preparing', icon: <FaFire /> },
  ready:     { label: 'Ready',     className: 'status-ready',     icon: <FaTruck /> },
  delivered: { label: 'Delivered', className: 'status-delivered', icon: <FaCheckCircle /> },
  cancelled: { label: 'Rejected',  className: 'status-cancelled', icon: <FaTimes /> },
};

const SIDEBAR_ITEMS = [
  { key: 'overview', label: 'Overview',  icon: <FaChartBar /> },
  { key: 'orders',   label: 'Orders',    icon: <FaClipboardList /> },
  { key: 'menu',     label: 'Menu',      icon: <FaUtensils /> },
  { key: 'settings', label: 'Settings',  icon: <FaCog /> },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
const AdminDashboard = () => {
  const { user, isLoggedIn, login, logout, loading: authLoading } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [menuOverrides, setMenuOverrides] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const pollRef = useRef(null);
  const prevOrderCountRef = useRef(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const isAdmin = isLoggedIn && user?.role === 'admin';

  /* ─── Fetch Orders ─── */
  const fetchOrders = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const res = await authAxios.get('/orders');
      if (res.data.success) {
        const newOrders = res.data.data;
        // Check for new pending orders
        const newPendingCount = newOrders.filter(o => o.status === 'pending').length;
        if (prevOrderCountRef.current > 0 && newPendingCount > prevOrderCountRef.current && soundEnabled) {
          playNotificationSound();
        }
        prevOrderCountRef.current = newPendingCount;
        setOrders(newOrders);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, soundEnabled]);

  /* ─── Fetch Settings ─── */
  const fetchSettings = useCallback(async () => {
    try {
      const res = await authAxios.get('/settings');
      if (res.data.success) setSettings(res.data.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  /* ─── Fetch Menu Overrides ─── */
  const fetchMenuOverrides = useCallback(async () => {
    try {
      const res = await authAxios.get('/settings/menu-overrides');
      if (res.data.success) setMenuOverrides(res.data.data);
    } catch (err) {
      console.error('Failed to fetch menu overrides:', err);
    }
  }, []);

  /* ─── Fetch Coupons ─── */
  const fetchCoupons = useCallback(async () => {
    try {
      const res = await authAxios.get('/coupons');
      if (res.data.success) setCoupons(res.data.data);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    }
  }, []);

  /* ─── Notification Sound ─── */
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gain.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => { oscillator.frequency.value = 1000; }, 150);
        setTimeout(() => { oscillator.frequency.value = 1200; }, 300);
        setTimeout(() => { oscillator.stop(); ctx.close(); }, 450);
      }
    } catch (e) {
      console.log('Audio not available');
    }
  }, []);

  /* ─── Init ─── */
  useEffect(() => {
    if (isAdmin) {
      fetchOrders();
      fetchSettings();
      fetchMenuOverrides();
      fetchCoupons();
      pollRef.current = setInterval(fetchOrders, 15000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isAdmin, fetchOrders, fetchSettings, fetchMenuOverrides, fetchCoupons]);

  /* ─── Login ─── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const loggedUser = await login(loginForm);
      if (loggedUser.role !== 'admin') {
        setLoginError('Access denied. Admin credentials required.');
        await logout();
      }
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  /* ─── Order Actions ─── */
  const updateOrderStatus = async (orderId, status, adminNotes) => {
    try {
      const body = { status };
      if (adminNotes) body.adminNotes = adminNotes;
      await authAxios.patch(`/orders/${orderId}/status`, body);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status, adminNotes: adminNotes || o.adminNotes } : o));
    } catch (err) {
      setError('Failed to update order status');
      setTimeout(() => setError(''), 3000);
    }
  };

  /* ─── Format helpers ─── */
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => `€${Number(amount || 0).toFixed(2)}`;

  /* ─── Computed ─── */
  const pendingOrders = useMemo(() => orders.filter(o => o.status === 'pending'), [orders]);
  const preparingOrders = useMemo(() => orders.filter(o => o.status === 'confirmed' || o.status === 'preparing'), [orders]);
  const deliveryOrders = useMemo(() => orders.filter(o => o.status === 'ready'), [orders]);
  const deliveredOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders.filter(o => o.status === 'delivered' && new Date(o.updatedAt) >= today);
  }, [orders]);
  const cancelledOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders.filter(o => o.status === 'cancelled' && new Date(o.updatedAt) >= today);
  }, [orders]);

  const todayOrders = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return orders.filter(o => new Date(o.createdAt) >= today);
  }, [orders]);

  const todayRevenue = useMemo(() =>
    todayOrders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + (o.total || 0), 0),
    [todayOrders]
  );

  const popularItems = useMemo(() => {
    const counts = {};
    todayOrders.forEach(o => {
      o.items?.forEach(item => {
        counts[item.name] = (counts[item.name] || 0) + item.quantity;
      });
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [todayOrders]);

  /* ─── AUTH LOADING ─── */
  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><div className="admin-spinner" /><p>Loading...</p></div>
      </div>
    );
  }

  /* ─── LOGIN SCREEN ─── */
  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-login-wrapper">
          <div className="admin-login-card">
            <div className="admin-login-header">
              <GiKnifeFork className="admin-login-logo" />
              <h1 className="admin-login-title">King Kebab</h1>
              <p className="admin-login-subtitle">Admin Dashboard</p>
            </div>
            <form className="admin-login-form" onSubmit={handleLogin}>
              <div className="admin-field">
                <label className="admin-label" htmlFor="admin-email"><FaEnvelope /> Email</label>
                <input id="admin-email" type="email" className="admin-input" placeholder="admin@kingkebab.com"
                  value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} required />
              </div>
              <div className="admin-field">
                <label className="admin-label" htmlFor="admin-password"><FaLock /> Password</label>
                <input id="admin-password" type="password" className="admin-input" placeholder="••••••••"
                  value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} required />
              </div>
              {loginError && <div className="admin-login-error"><FaExclamationTriangle /> {loginError}</div>}
              <button type="submit" className="admin-login-btn" disabled={loginLoading}>
                {loginLoading ? <><span className="btn-spinner" /> Signing in...</> : <><FaSignInAlt /> Sign In</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     ADMIN DASHBOARD (LOGGED IN)
     ════════════════════════════════════════════════════════════════ */
  return (
    <div className="admin-page">
      {/* ── Top Header ── */}
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <span /><span /><span />
          </button>
          <GiKnifeFork className="admin-header-logo" />
          <div>
            <h1 className="admin-header-title">King Kebab</h1>
            <span className="admin-header-sub">Admin Dashboard</span>
          </div>
        </div>
        <div className="admin-header-right">
          {pendingOrders.length > 0 && (
            <button className="admin-notif-btn pulse-gold" onClick={() => setActiveSection('orders')} title="Pending orders">
              <FaBell />
              <span className="notif-count">{pendingOrders.length}</span>
            </button>
          )}
          <button className="admin-sound-btn" onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}>
            {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
          </button>
          <button className="admin-refresh-btn" onClick={fetchOrders} title="Refresh orders">
            <FaSync className={loading ? 'spinning' : ''} />
          </button>
          <div className="admin-user-badge"><FaUser /> {user?.name}</div>
          <button className="admin-logout-btn" onClick={logout}><FaSignOutAlt /> Logout</button>
        </div>
      </header>

      <div className="admin-layout">
        {/* ── Sidebar ── */}
        <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <nav className="sidebar-nav">
            {SIDEBAR_ITEMS.map(item => (
              <button
                key={item.key}
                className={`sidebar-item ${activeSection === item.key ? 'active' : ''}`}
                onClick={() => setActiveSection(item.key)}
                title={item.label}
              >
                <span className="sidebar-icon">{item.icon}</span>
                <span className="sidebar-label">{item.label}</span>
                {item.key === 'orders' && pendingOrders.length > 0 && (
                  <span className="sidebar-badge">{pendingOrders.length}</span>
                )}
              </button>
            ))}
          </nav>
          {settings?.isPaused && (
            <div className="sidebar-pause-banner">
              <FaPause /> Orders Paused
            </div>
          )}
        </aside>

        {/* ── Main Content ── */}
        <main className="admin-main">
          {error && <div className="admin-global-error"><FaExclamationTriangle /> {error}</div>}

          {activeSection === 'overview' && (
            <OverviewSection
              orders={orders} todayOrders={todayOrders} todayRevenue={todayRevenue}
              pendingOrders={pendingOrders} preparingOrders={preparingOrders}
              deliveredOrders={deliveredOrders} popularItems={popularItems}
              formatDate={formatDate} formatCurrency={formatCurrency}
              setActiveSection={setActiveSection}
            />
          )}
          {activeSection === 'orders' && (
            <OrdersSection
              orders={orders} pendingOrders={pendingOrders} preparingOrders={preparingOrders}
              deliveryOrders={deliveryOrders} deliveredOrders={deliveredOrders}
              cancelledOrders={cancelledOrders} updateOrderStatus={updateOrderStatus}
              formatDate={formatDate} formatCurrency={formatCurrency} loading={loading}
            />
          )}
          {activeSection === 'menu' && (
            <MenuSection
              menuOverrides={menuOverrides} setMenuOverrides={setMenuOverrides}
              formatCurrency={formatCurrency}
            />
          )}
          {activeSection === 'settings' && (
            <SettingsSection
              settings={settings}
              setSettings={setSettings}
              coupons={coupons}
              setCoupons={setCoupons}
              fetchSettings={fetchSettings}
            />
          )}
        </main>
      </div>
    </div>
  );
};

/* ================================================================
   OVERVIEW SECTION
   ================================================================ */
const OverviewSection = ({ orders, todayOrders, todayRevenue, pendingOrders, preparingOrders, deliveredOrders, popularItems, formatDate, formatCurrency, setActiveSection }) => {
  const avgOrderValue = todayOrders.length > 0 ? todayRevenue / todayOrders.filter(o => o.status !== 'cancelled').length : 0;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><FaChartBar /> Dashboard Overview</h2>
        <span className="section-subtitle">Today's performance at a glance</span>
      </div>

      {/* Stats Cards */}
      <div className="overview-stats">
        <div className="stat-card stat-card-gold" onClick={() => setActiveSection('orders')}>
          <div className="stat-icon"><FaClipboardList /></div>
          <div className="stat-info">
            <span className="stat-value">{todayOrders.length}</span>
            <span className="stat-name">Today's Orders</span>
          </div>
        </div>
        <div className="stat-card stat-card-green">
          <div className="stat-icon"><FaEuroSign /></div>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(todayRevenue)}</span>
            <span className="stat-name">Today's Revenue</span>
          </div>
        </div>
        <div className="stat-card stat-card-blue">
          <div className="stat-icon"><FaChartBar /></div>
          <div className="stat-info">
            <span className="stat-value">{formatCurrency(avgOrderValue || 0)}</span>
            <span className="stat-name">Avg Order Value</span>
          </div>
        </div>
        <div className="stat-card stat-card-orange" onClick={() => setActiveSection('orders')}>
          <div className="stat-icon"><FaBell /></div>
          <div className="stat-info">
            <span className="stat-value">{pendingOrders.length}</span>
            <span className="stat-name">Pending Orders</span>
          </div>
        </div>
      </div>

      <div className="overview-grid">
        {/* Recent Orders */}
        <div className="overview-card">
          <h3 className="overview-card-title"><FaHistory /> Recent Orders</h3>
          {todayOrders.length === 0 ? (
            <p className="overview-empty">No orders today yet</p>
          ) : (
            <div className="recent-orders-list">
              {todayOrders.slice(0, 8).map(order => {
                const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                return (
                  <div className="recent-order-row" key={order._id}>
                    <div className="recent-order-left">
                      <span className="recent-order-id">#{order._id.slice(-6).toUpperCase()}</span>
                      <span className="recent-order-name">{order.customer?.name}</span>
                    </div>
                    <div className="recent-order-right">
                      <span className={`mini-badge ${sc.className}`}>{sc.label}</span>
                      <span className="recent-order-total">{formatCurrency(order.total)}</span>
                      <span className="recent-order-time">{formatDate(order.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Popular Items */}
        <div className="overview-card">
          <h3 className="overview-card-title"><FaFire /> Popular Items Today</h3>
          {popularItems.length === 0 ? (
            <p className="overview-empty">No items ordered yet</p>
          ) : (
            <div className="popular-items-list">
              {popularItems.map(([name, count], i) => (
                <div className="popular-item-row" key={name}>
                  <span className="popular-rank">{i + 1}</span>
                  <span className="popular-name">{name}</span>
                  <span className="popular-count">{count} sold</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="overview-card">
          <h3 className="overview-card-title"><FaClipboardList /> Order Status</h3>
          <div className="status-breakdown">
            <div className="status-row"><span className="status-dot pending-dot" />Pending<span className="status-count">{pendingOrders.length}</span></div>
            <div className="status-row"><span className="status-dot preparing-dot" />Preparing<span className="status-count">{preparingOrders.length}</span></div>
            <div className="status-row"><span className="status-dot delivered-dot" />Delivered Today<span className="status-count">{deliveredOrders.length}</span></div>
            <div className="status-row"><span className="status-dot total-dot" />All Time<span className="status-count">{orders.length}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   ORDERS SECTION — KANBAN BOARD
   ================================================================ */
const OrdersSection = ({ orders, pendingOrders, preparingOrders, deliveryOrders, deliveredOrders, cancelledOrders, updateOrderStatus, formatDate, formatCurrency, loading }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showRejected, setShowRejected] = useState(false);

  const filterOrders = (orderList) => {
    if (!searchTerm) return orderList;
    const q = searchTerm.toLowerCase();
    return orderList.filter(o =>
      o.customer?.name?.toLowerCase().includes(q) ||
      o.customer?.phone?.includes(q) ||
      o._id.toLowerCase().includes(q)
    );
  };

  const handleReject = (orderId) => {
    updateOrderStatus(orderId, 'cancelled', rejectReason || 'Order rejected by admin');
    setRejectingOrder(null);
    setRejectReason('');
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <div>
          <h2 className="section-title"><FaClipboardList /> Order Management</h2>
          <span className="section-subtitle">Drag orders through the pipeline</span>
        </div>
        <div className="section-actions">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input type="text" placeholder="Search orders..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)} className="search-input" id="order-search" />
          </div>
          <button className={`toggle-btn ${showHistory ? 'active' : ''}`} onClick={() => setShowHistory(!showHistory)}>
            <FaHistory /> History
          </button>
        </div>
      </div>

      {!showHistory ? (
        <>
          {/* Kanban Board */}
          <div className="kanban-board">
            {/* Column 1: Incoming */}
            <div className="kanban-column kanban-incoming">
              <div className="kanban-header">
                <span className="kanban-title"><FaBell /> Incoming</span>
                <span className="kanban-count">{filterOrders(pendingOrders).length}</span>
              </div>
              <div className="kanban-cards">
                {filterOrders(pendingOrders).length === 0 ? (
                  <div className="kanban-empty">No pending orders</div>
                ) : filterOrders(pendingOrders).map(order => (
                  <OrderCard key={order._id} order={order} expanded={expandedOrder === order._id}
                    onToggle={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                    formatDate={formatDate} formatCurrency={formatCurrency}
                    actions={
                      rejectingOrder === order._id ? (
                        <div className="reject-form">
                          <input type="text" className="reject-reason-input" placeholder="Reason (optional)"
                            value={rejectReason} onChange={e => setRejectReason(e.target.value)} autoFocus />
                          <div className="reject-form-btns">
                            <button className="reject-confirm-btn" onClick={() => handleReject(order._id)}><FaTimes /> Reject</button>
                            <button className="reject-cancel-btn" onClick={() => { setRejectingOrder(null); setRejectReason(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="order-actions">
                          <button className="order-accept-btn" onClick={() => updateOrderStatus(order._id, 'confirmed')}>
                            <FaCheck /> Accept
                          </button>
                          <button className="order-reject-btn" onClick={() => setRejectingOrder(order._id)}>
                            <FaTimes /> Reject
                          </button>
                        </div>
                      )
                    }
                  />
                ))}
              </div>
            </div>

            {/* Column 2: Preparing */}
            <div className="kanban-column kanban-preparing">
              <div className="kanban-header">
                <span className="kanban-title"><FaFire /> Preparing</span>
                <span className="kanban-count">{filterOrders(preparingOrders).length}</span>
              </div>
              <div className="kanban-cards">
                {filterOrders(preparingOrders).length === 0 ? (
                  <div className="kanban-empty">No orders being prepared</div>
                ) : filterOrders(preparingOrders).map(order => (
                  <OrderCard key={order._id} order={order} expanded={expandedOrder === order._id}
                    onToggle={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                    formatDate={formatDate} formatCurrency={formatCurrency}
                    actions={
                      <div className="order-actions">
                        <button className="order-ready-btn" onClick={() => updateOrderStatus(order._id, 'ready')}>
                          <FaTruck /> Ready for Delivery
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            </div>

            {/* Column 3: Out for Delivery */}
            <div className="kanban-column kanban-delivery">
              <div className="kanban-header">
                <span className="kanban-title"><FaTruck /> Out for Delivery</span>
                <span className="kanban-count">{filterOrders(deliveryOrders).length}</span>
              </div>
              <div className="kanban-cards">
                {filterOrders(deliveryOrders).length === 0 ? (
                  <div className="kanban-empty">No deliveries in progress</div>
                ) : filterOrders(deliveryOrders).map(order => (
                  <OrderCard key={order._id} order={order} expanded={expandedOrder === order._id}
                    onToggle={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                    formatDate={formatDate} formatCurrency={formatCurrency}
                    actions={
                      <div className="order-actions">
                        <button className="order-delivered-btn" onClick={() => updateOrderStatus(order._id, 'delivered')}>
                          <FaCheckCircle /> Mark Delivered
                        </button>
                      </div>
                    }
                  />
                ))}
              </div>
            </div>

            {/* Column 4: Delivered */}
            <div className="kanban-column kanban-delivered">
              <div className="kanban-header">
                <span className="kanban-title"><FaCheckCircle /> Delivered</span>
                <span className="kanban-count">{filterOrders(deliveredOrders).length}</span>
              </div>
              <div className="kanban-cards">
                {filterOrders(deliveredOrders).length === 0 ? (
                  <div className="kanban-empty">No delivered orders today</div>
                ) : filterOrders(deliveredOrders).map(order => (
                  <OrderCard key={order._id} order={order} expanded={expandedOrder === order._id}
                    onToggle={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                    formatDate={formatDate} formatCurrency={formatCurrency} actions={null}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Rejected Orders Collapsible */}
          {cancelledOrders.length > 0 && (
            <div className="rejected-section">
              <button className="rejected-toggle" onClick={() => setShowRejected(!showRejected)}>
                <FaBan /> Rejected Orders ({cancelledOrders.length})
                {showRejected ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              {showRejected && (
                <div className="rejected-grid">
                  {cancelledOrders.map(order => (
                    <OrderCard key={order._id} order={order} expanded={expandedOrder === order._id}
                      onToggle={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                      formatDate={formatDate} formatCurrency={formatCurrency} actions={null}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <OrderHistory formatDate={formatDate} formatCurrency={formatCurrency} />
      )}
    </div>
  );
};

/* ================================================================
   ORDER CARD (reusable)
   ================================================================ */
const OrderCard = ({ order, expanded, onToggle, formatDate, formatCurrency, actions }) => {
  const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const isPending = order.status === 'pending';

  return (
    <div className={`order-card ${isPending ? 'order-pending-glow' : ''}`}>
      {/* Header */}
      <div className="order-card-header" onClick={onToggle}>
        <div className="order-card-header-left">
          <span className="order-id"><FaHashtag />{order._id.slice(-6).toUpperCase()}</span>
          <span className={`order-status-badge ${sc.className}`}>{sc.icon} {sc.label}</span>
        </div>
        <div className="order-card-header-right">
          <span className="order-time"><FaClock /> {formatDate(order.createdAt)}</span>
          {expanded ? <FaChevronUp className="order-chevron" /> : <FaChevronDown className="order-chevron" />}
        </div>
      </div>

      {/* Quick Info */}
      <div className="order-quick-info">
        <div className="oqi-row">
          <FaUser className="oqi-icon" />
          <span className="oqi-value">{order.customer?.name}</span>
        </div>
        <div className="oqi-row">
          <FaPhone className="oqi-icon" />
          <span className="oqi-value">{order.customer?.phone}</span>
        </div>
        <div className="oqi-row">
          <FaTruck className="oqi-icon" />
          <span className="oqi-value">{order.orderType === 'takeaway' ? 'Takeaway' : 'Delivery'}</span>
        </div>
        <div className="oqi-row oqi-total">
          <FaEuroSign className="oqi-icon" />
          <span className="oqi-value">{formatCurrency(order.total)}</span>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="order-details">
          {/* Email */}
          <div className="detail-row">
            <FaEnvelope className="detail-icon" />
            <div><span className="detail-label">Email</span><span className="detail-value">{order.customer?.email}</span></div>
          </div>
          {/* Address */}
          <div className="detail-row">
            <FaMapMarkerAlt className="detail-icon" />
            <div>
              <span className="detail-label">{order.orderType === 'takeaway' ? 'Order Type' : 'Delivery Address'}</span>
              {order.orderType === 'takeaway' ? (
                <span className="detail-value">Takeaway (Pickup at restaurant)</span>
              ) : (
                <span className="detail-value">
                  {order.deliveryAddress?.street}, {order.deliveryAddress?.city}
                  {order.deliveryAddress?.eircode ? ` — ${order.deliveryAddress.eircode}` : ''}
                </span>
              )}
              {order.orderType !== 'takeaway' && order.deliveryAddress?.notes && (
                <span className="detail-notes"><FaStickyNote /> {order.deliveryAddress.notes}</span>
              )}
            </div>
          </div>
          {/* Items */}
          <div className="detail-items">
            <span className="detail-label"><FaUtensils /> Items Ordered</span>
            <div className="items-table">
              {order.items?.map((item, i) => (
                <div className="item-row" key={i}>
                  <span className="item-name">{item.name}</span>
                  <span className="item-qty">×{item.quantity}</span>
                  <span className="item-price">{formatCurrency(item.price * item.quantity)}</span>
                </div>
              ))}
              <div className="item-row item-total-row">
                <span className="item-name">Total</span>
                <span></span>
                <span className="item-price">{formatCurrency(order.total)}</span>
              </div>
            </div>
          </div>
          {/* Payment Method */}
          {order.paymentMethod && (
            <div className="detail-row">
              <FaEuroSign className="detail-icon" />
              <div>
                <span className="detail-label">Payment Method</span>
                <span className={`detail-value detail-payment-badge pm-${order.paymentMethod}`}>
                  {order.paymentMethod === 'card' && '💳 Card'}
                  {order.paymentMethod === 'bank_transfer' && '🏦 Bank Transfer'}
                  {order.paymentMethod === 'revolut' && '💳 Revolut'}
                  {order.paymentMethod === 'cash_on_delivery' && '💵 Cash on Delivery'}
                </span>
              </div>
            </div>
          )}
          {/* Coupon */}
          {order.couponCode && (
            <div className="detail-row">
              <FaStickyNote className="detail-icon" />
              <div>
                <span className="detail-label">Coupon Applied</span>
                <span className="detail-value" style={{ color: '#4ecb71' }}>
                  {order.couponCode} — saved {formatCurrency(order.discount)}
                </span>
              </div>
            </div>
          )}
          {order.adminNotes && (
            <div className="detail-admin-notes"><FaStickyNote /> <em>{order.adminNotes}</em></div>
          )}
        </div>
      )}

      {/* Actions */}
      {actions}
    </div>
  );
};

/* ================================================================
   ORDER HISTORY
   ================================================================ */
const OrderHistory = ({ formatDate, formatCurrency }) => {
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (searchTerm) params.set('search', searchTerm);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (dateFrom) params.set('from', dateFrom);
      if (dateTo) params.set('to', dateTo);
      const res = await authAxios.get(`/orders/history?${params}`);
      if (res.data.success) {
        setHistoryOrders(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [searchTerm, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div className="history-section">
      <div className="history-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Search by name, email, phone..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} className="search-input" />
        </div>
        <select className="history-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Rejected</option>
          <option value="confirmed">Accepted</option>
          <option value="pending">Pending</option>
        </select>
        <input type="date" className="history-date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span className="history-date-sep">to</span>
        <input type="date" className="history-date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
      </div>

      {historyLoading ? (
        <div className="admin-loading"><div className="admin-spinner" /><p>Loading history...</p></div>
      ) : historyOrders.length === 0 ? (
        <div className="kanban-empty" style={{ padding: '60px 0' }}>No orders found matching your filters</div>
      ) : (
        <>
          <div className="history-table">
            <div className="history-table-header">
              <span>Order ID</span><span>Customer</span><span>Items</span><span>Total</span><span>Status</span><span>Date</span>
            </div>
            {historyOrders.map(order => {
              const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              return (
                <div className="history-table-row" key={order._id} onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}>
                  <span className="ht-id">#{order._id.slice(-6).toUpperCase()}</span>
                  <span className="ht-customer">{order.customer?.name}</span>
                  <span className="ht-items">{order.items?.length} items</span>
                  <span className="ht-total">{formatCurrency(order.total)}</span>
                  <span className={`mini-badge ${sc.className}`}>{sc.label}</span>
                  <span className="ht-date">{formatDate(order.createdAt)}</span>
                  {expandedOrder === order._id && (
                    <div className="ht-expanded" onClick={e => e.stopPropagation()}>
                      <p><FaPhone /> {order.customer?.phone} &nbsp; <FaEnvelope /> {order.customer?.email}</p>
                      <p>
                        <FaMapMarkerAlt /> {order.orderType === 'takeaway'
                          ? 'Takeaway (Pickup at restaurant)'
                          : `${order.deliveryAddress?.street}, ${order.deliveryAddress?.city}`}
                      </p>
                      <div className="ht-items-list">
                        {order.items?.map((item, i) => (
                          <span key={i}>{item.name} ×{item.quantity}</span>
                        ))}
                      </div>
                      {order.adminNotes && <p className="ht-notes"><FaStickyNote /> {order.adminNotes}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {pagination.pages > 1 && (
            <div className="history-pagination">
              {Array.from({ length: pagination.pages }, (_, i) => (
                <button key={i} className={`page-btn ${pagination.page === i + 1 ? 'active' : ''}`}
                  onClick={() => fetchHistory(i + 1)}>{i + 1}</button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ================================================================
   MENU MANAGEMENT SECTION
   ================================================================ */
const MenuSection = ({ menuOverrides, setMenuOverrides, formatCurrency }) => {
  const [activeCategory, setActiveCategory] = useState('deals');
  const [editingItem, setEditingItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const categories = Object.keys(MENU_DATA);
  const currentItems = MENU_DATA[activeCategory]?.items || [];

  const getOverride = (itemId) => menuOverrides.find(o => o.itemId === itemId);

  const toggleAvailability = async (itemId) => {
    const existing = getOverride(itemId);
    const newAvailable = existing ? !existing.available : false;
    try {
      setSaving(true);
      const res = await authAxios.patch(`/settings/menu/${itemId}`, { available: newAvailable });
      if (res.data.success) setMenuOverrides(res.data.data);
    } catch (err) {
      console.error('Failed to toggle availability:', err);
    } finally {
      setSaving(false);
    }
  };

  const savePrice = async (itemId) => {
    if (!editPrice || isNaN(editPrice)) return;
    try {
      setSaving(true);
      const res = await authAxios.patch(`/settings/menu/${itemId}`, { price: parseFloat(editPrice) });
      if (res.data.success) setMenuOverrides(res.data.data);
      setEditingItem(null);
      setEditPrice('');
    } catch (err) {
      console.error('Failed to update price:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><FaUtensils /> Menu Management</h2>
        <span className="section-subtitle">Toggle availability and edit prices</span>
      </div>

      {/* Category Tabs */}
      <div className="menu-admin-tabs">
        {categories.map(cat => (
          <button key={cat} className={`menu-admin-tab ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}>
            {CATEGORY_META[cat]?.label || cat}
            <span className="tab-count">{MENU_DATA[cat]?.items?.length || 0}</span>
          </button>
        ))}
      </div>

      {/* Items Grid */}
      <div className="menu-admin-grid">
        {currentItems.map(item => {
          const override = getOverride(item._id);
          const isAvailable = override ? override.available : true;
          const displayPrice = override?.price ?? item.price;
          const isEditing = editingItem === item._id;

          return (
            <div className={`menu-admin-item ${!isAvailable ? 'sold-out' : ''}`} key={item._id}>
              <div className="mai-top">
                <div className="mai-info">
                  <h4 className="mai-name">{item.name}</h4>
                  <p className="mai-desc">{item.desc}</p>
                </div>
                <button className={`mai-toggle ${isAvailable ? 'on' : 'off'}`}
                  onClick={() => toggleAvailability(item._id)} disabled={saving}
                  title={isAvailable ? 'Mark as Sold Out' : 'Mark as Available'}>
                  {isAvailable ? <FaToggleOn /> : <FaToggleOff />}
                  <span>{isAvailable ? 'Available' : 'Sold Out'}</span>
                </button>
              </div>
              <div className="mai-bottom">
                {isEditing ? (
                  <div className="mai-edit-price">
                    <span className="mai-euro">€</span>
                    <input type="number" step="0.01" value={editPrice} onChange={e => setEditPrice(e.target.value)}
                      className="mai-price-input" autoFocus />
                    <button className="mai-save-btn" onClick={() => savePrice(item._id)} disabled={saving}><FaSave /></button>
                    <button className="mai-cancel-btn" onClick={() => { setEditingItem(null); setEditPrice(''); }}><FaTimes /></button>
                  </div>
                ) : (
                  <div className="mai-price-display">
                    <span className="mai-price">€{Number(displayPrice).toFixed(2)}</span>
                    {override?.price && override.price !== item.price && (
                      <span className="mai-original">was €{item.price.toFixed(2)}</span>
                    )}
                    <button className="mai-edit-btn" onClick={() => { setEditingItem(item._id); setEditPrice(String(displayPrice)); }}
                      title="Edit price"><FaEdit /></button>
                  </div>
                )}
                {!isAvailable && <span className="mai-sold-badge"><FaBan /> SOLD OUT</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ================================================================
   SETTINGS SECTION
   ================================================================ */
const SettingsSection = ({ settings, setSettings, coupons, setCoupons, fetchSettings }) => {
  const [saving, setSaving] = useState(false);
  const [couponSaving, setCouponSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [localSettings, setLocalSettings] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    type: 'percent',
    value: '',
    description: '',
  });

  useEffect(() => {
    if (settings) setLocalSettings(JSON.parse(JSON.stringify(settings)));
  }, [settings]);

  if (!localSettings) return <div className="admin-loading"><div className="admin-spinner" /><p>Loading settings...</p></div>;

  const updateDay = (day, field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: { ...prev.openingHours[day], [field]: value },
      },
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await authAxios.patch('/settings', {
        openingHours: localSettings.openingHours,
        deliveryFee: localSettings.deliveryFee,
        deliveryZone: localSettings.deliveryZone,
        isPaused: localSettings.isPaused,
        pauseMessage: localSettings.pauseMessage,
      });
      if (res.data.success) {
        setSettings(res.data.data);
        setSuccessMsg('Settings saved successfully!');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    if (!couponForm.code.trim() || !couponForm.value) return;
    try {
      setCouponSaving(true);
      const res = await authAxios.post('/coupons', {
        code: couponForm.code.trim().toUpperCase(),
        type: couponForm.type,
        value: Number(couponForm.value),
        description: couponForm.description.trim(),
      });
      if (res.data.success) {
        setCoupons(prev => [res.data.data, ...prev]);
        setCouponForm({ code: '', type: 'percent', value: '', description: '' });
      }
    } catch (err) {
      console.error('Failed to create coupon:', err);
    } finally {
      setCouponSaving(false);
    }
  };

  const toggleCouponActive = async (coupon) => {
    try {
      setCouponSaving(true);
      const res = await authAxios.patch(`/coupons/${coupon._id}`, { isActive: !coupon.isActive });
      if (res.data.success) {
        setCoupons(prev => prev.map(c => (c._id === coupon._id ? res.data.data : c)));
      }
    } catch (err) {
      console.error('Failed to update coupon:', err);
    } finally {
      setCouponSaving(false);
    }
  };

  const deleteCoupon = async (couponId) => {
    try {
      setCouponSaving(true);
      await authAxios.delete(`/coupons/${couponId}`);
      setCoupons(prev => prev.filter(c => c._id !== couponId));
    } catch (err) {
      console.error('Failed to delete coupon:', err);
    } finally {
      setCouponSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 className="section-title"><FaCog /> Store Settings</h2>
        <button className="save-settings-btn" onClick={saveSettings} disabled={saving}>
          {saving ? <><span className="btn-spinner" /> Saving...</> : <><FaSave /> Save All Changes</>}
        </button>
      </div>
      {successMsg && <div className="settings-success"><FaCheck /> {successMsg}</div>}

      <div className="settings-grid">
        {/* Order Pause */}
        <div className="settings-card settings-pause">
          <h3 className="settings-card-title"><FaPause /> Order Pause</h3>
          <p className="settings-card-desc">Temporarily stop accepting new orders</p>
          <div className="pause-toggle-row">
            <button className={`pause-btn ${localSettings.isPaused ? 'paused' : 'active'}`}
              onClick={() => setLocalSettings({ ...localSettings, isPaused: !localSettings.isPaused })}>
              {localSettings.isPaused ? <><FaPlay /> Resume Orders</> : <><FaPause /> Pause Orders</>}
            </button>
            <span className={`pause-status ${localSettings.isPaused ? 'paused' : ''}`}>
              {localSettings.isPaused ? '⏸ Orders are PAUSED' : '✅ Accepting orders'}
            </span>
          </div>
          {localSettings.isPaused && (
            <div className="admin-field" style={{ marginTop: 16 }}>
              <label className="admin-label">Pause Message</label>
              <input type="text" className="admin-input" value={localSettings.pauseMessage}
                onChange={e => setLocalSettings({ ...localSettings, pauseMessage: e.target.value })} />
            </div>
          )}
        </div>

        {/* Delivery Settings */}
        <div className="settings-card">
          <h3 className="settings-card-title"><FaTruck /> Delivery</h3>
          <div className="admin-field">
            <label className="admin-label"><FaEuroSign /> Delivery Fee</label>
            <input type="number" step="0.50" className="admin-input" value={localSettings.deliveryFee}
              onChange={e => setLocalSettings({ ...localSettings, deliveryFee: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="admin-field" style={{ marginTop: 16 }}>
            <label className="admin-label"><FaMapPin /> Delivery Zone</label>
            <input type="text" className="admin-input" value={localSettings.deliveryZone}
              onChange={e => setLocalSettings({ ...localSettings, deliveryZone: e.target.value })} />
          </div>
        </div>

        {/* Opening Hours */}
        <div className="settings-card settings-hours">
          <h3 className="settings-card-title"><FaClock /> Opening Hours</h3>
          <div className="hours-grid">
            {DAYS.map(day => {
              const dayData = localSettings.openingHours?.[day] || { open: '11:00', close: '23:00', isClosed: false };
              return (
                <div className={`hours-row ${dayData.isClosed ? 'closed-day' : ''}`} key={day}>
                  <span className="hours-day">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                  <label className="hours-closed-toggle">
                    <input type="checkbox" checked={dayData.isClosed}
                      onChange={e => updateDay(day, 'isClosed', e.target.checked)} />
                    <span>Closed</span>
                  </label>
                  {!dayData.isClosed && (
                    <>
                      <input type="time" className="hours-time" value={dayData.open}
                        onChange={e => updateDay(day, 'open', e.target.value)} />
                      <span className="hours-sep">—</span>
                      <input type="time" className="hours-time" value={dayData.close}
                        onChange={e => updateDay(day, 'close', e.target.value)} />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="settings-card coupon-admin-card">
        <h3 className="settings-card-title"><FaHashtag /> Coupon Management</h3>
        <p className="settings-card-desc">Create, enable/disable, and remove customer coupons</p>

        <form className="coupon-admin-form" onSubmit={createCoupon}>
          <div className="coupon-admin-row">
            <input
              type="text"
              className="admin-input"
              placeholder="Code (e.g. WELCOME10)"
              value={couponForm.code}
              onChange={e => setCouponForm({ ...couponForm, code: e.target.value })}
              required
            />
            <select
              className="admin-input"
              value={couponForm.type}
              onChange={e => setCouponForm({ ...couponForm, type: e.target.value })}
            >
              <option value="percent">Percent</option>
              <option value="flat">Flat (€)</option>
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              className="admin-input"
              placeholder={couponForm.type === 'percent' ? 'Value (%)' : 'Value (€)'}
              value={couponForm.value}
              onChange={e => setCouponForm({ ...couponForm, value: e.target.value })}
              required
            />
            <button type="submit" className="save-settings-btn" disabled={couponSaving}>
              <FaPlus /> Add
            </button>
          </div>
          <input
            type="text"
            className="admin-input"
            placeholder="Description (optional)"
            value={couponForm.description}
            onChange={e => setCouponForm({ ...couponForm, description: e.target.value })}
          />
        </form>

        <div className="coupon-admin-list">
          {coupons.length === 0 ? (
            <p className="overview-empty">No coupons created yet</p>
          ) : (
            coupons.map(coupon => (
              <div className="coupon-admin-item" key={coupon._id}>
                <div>
                  <div className="coupon-admin-code">{coupon.code}</div>
                  <div className="coupon-admin-meta">
                    {coupon.type === 'percent' ? `${coupon.value}% off` : `€${Number(coupon.value).toFixed(2)} off`}
                    {coupon.description ? ` • ${coupon.description}` : ''}
                  </div>
                </div>
                <div className="coupon-admin-actions">
                  <button
                    type="button"
                    className={`toggle-btn ${coupon.isActive ? 'active' : ''}`}
                    onClick={() => toggleCouponActive(coupon)}
                    disabled={couponSaving}
                  >
                    {coupon.isActive ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    type="button"
                    className="order-reject-btn"
                    onClick={() => deleteCoupon(coupon._id)}
                    disabled={couponSaving}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
